/**
 * 盤面の状態と、その健全性の検証。
 *
 * 根拠: spec/04a-puzzle-core.md §1.3 / §8。
 */

import type { CellIndex, Placement, PositionOf, TargetLayout } from './types';

/**
 * 盤面 1 面ぶんの状態。
 *
 * `placement` を正とし、`positionOf` は必要に応じて `positionsOf` で導出する
 * （`04a` §1.3）。`target` を盤面の一部として持つのは、不変条件 I-2 以降の判定が
 * 配置と目標配置の両方を同時に必要とするため。
 */
export interface Board {
  /** 列数。 */
  readonly C: number;
  /** 行数。 */
  readonly R: number;
  /** 各セルにいるピース。 */
  readonly placement: Placement;
  /** 各ピースの正解セル。段階によって `τ` と `id` が入れ替わる。 */
  readonly target: TargetLayout;
}

/**
 * `placement` の逆写像を作る。
 *
 * 入力が全単射である（不変条件 I-1）ことを前提とする。破れている入力を渡した場合、
 * 到達しなかったピースの位置は `-1` のまま残るため、`assertInvariants` が捕捉できる。
 */
export function positionsOf(placement: Placement): PositionOf {
  const positionOf: PositionOf = new Array<CellIndex>(placement.length).fill(-1);
  for (const [cell, piece] of placement.entries()) {
    positionOf[piece] = cell;
  }
  return positionOf;
}

/**
 * 不変条件 I-1（`placement` が全単射）と I-6（`target` が全単射）を検証し、
 * 破れていれば例外を投げる。
 *
 * 開発ビルドではドロップ確定のたびに呼ぶ（`04a` §8）。本番ビルドでは `__DEV__` が
 * `false` に畳まれ、本関数の中身ごと消える。
 */
export function assertInvariants(board: Board): void {
  // 本番ビルドでのみ通る枝。テストでは __DEV__ がリテラルの true に畳まれるため到達できない。
  /* v8 ignore next */
  if (!__DEV__) return;
  const N = board.C * board.R;
  assertBijection(board.placement, N, 'I-1', 'placement');
  assertBijection(board.target, N, 'I-6', 'target');
}

/** `xs` が `0..N−1` の全単射であることを検証する。 */
function assertBijection(xs: readonly number[], N: number, id: string, name: string): void {
  if (xs.length !== N) {
    throw new Error(
      `不変条件 ${id} 違反: ${name} の長さが ${xs.length} で、盤面のセル数 ${N} と一致しません。`,
    );
  }
  const seen = new Uint8Array(N);
  for (const [i, x] of xs.entries()) {
    if (!Number.isInteger(x) || x < 0 || x >= N) {
      throw new Error(
        `不変条件 ${id} 違反: ${name}[${i}] = ${x} が 0..${N - 1} の範囲に収まっていません。`,
      );
    }
    if (seen[x] === 1) {
      throw new Error(`不変条件 ${id} 違反: ${name} で値 ${x} が重複しています（${name}[${i}]）。`);
    }
    seen[x] = 1;
  }
}
